import React from "react";
import { TrendingUp } from "lucide-react";

// Mirrors the backend policy (auth_service.validate_password) for instant feedback only.
// The server is the authority and re-validates every password.
export const PASSWORD_MIN_CHARS = 12;
export const PASSWORD_MAX_BYTES = 72;

export function passwordProblems(password, confirm, email) {
  const problems = [];
  const bytes = new TextEncoder().encode(password || "").length;
  if ((password || "").length < PASSWORD_MIN_CHARS) problems.push(`At least ${PASSWORD_MIN_CHARS} characters`);
  if (bytes > PASSWORD_MAX_BYTES) problems.push(`At most ${PASSWORD_MAX_BYTES} bytes`);
  const local = (email || "").toLowerCase().split("@")[0];
  if (local && local.length >= 4 && (password || "").toLowerCase().includes(local)) problems.push("Must not contain your email");
  if (confirm !== undefined && password !== confirm) problems.push("Passwords must match");
  return problems;
}

export const inputCls = "w-full bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-3 py-2 text-sm mono focus:border-[#2563EB] outline-none disabled:opacity-60";
export const labelCls = "text-[11px] text-slate-400 uppercase tracking-wide";
export const primaryBtn = "gz-btn gz-btn-primary";
export const ghostBtn = "gz-btn gz-btn-ghost";
export const dangerBtn = "gz-btn gz-btn-danger";

export function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ children, testId }) {
  if (!children) return null;
  return <div data-testid={testId} className="text-xs text-[#FF6b62] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm px-3 py-2">{children}</div>;
}

export function NoticeBox({ children, testId }) {
  if (!children) return null;
  return <div data-testid={testId} className="text-xs text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm px-3 py-2">{children}</div>;
}

export function PasswordRules({ password, confirm, email }) {
  if (!password && !confirm) {
    return <div className="text-[10px] text-slate-500">At least {PASSWORD_MIN_CHARS} characters. Avoid common passwords and your email.</div>;
  }
  const problems = passwordProblems(password, confirm, email);
  return problems.length === 0
    ? <div className="text-[10px] text-[#00C805]">Looks good</div>
    : <ul className="text-[10px] text-slate-400 list-disc pl-4">{problems.map((p) => <li key={p}>{p}</li>)}</ul>;
}

// Centered card used by the pages that run outside the main terminal layout.
export function AuthShell({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] px-4">
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <TrendingUp className="h-6 w-6 text-[#2563EB]" />
          <div>
            <div className="text-lg font-black tracking-tight">GROUNDZERO</div>
            <div className="text-[10px] text-slate-500 mono tracking-widest">GZ</div>
          </div>
        </div>
        <div className="term-panel rounded-sm p-6 space-y-4" data-testid={testId}>
          <div>
            <div className="text-lg font-semibold tracking-tight">{title}</div>
            {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
